import { defineStore } from 'pinia'
import { ref } from 'vue'
import { keyBy } from 'lodash'
import type {
  WorkflowProgressData,
  WorkflowProgressResponse,
  WorkflowProgressItemKey,
} from '@app/application/workflows/types'
import decompressWorkflowProgresses from '@app/application/workflows/utils/workflow_progress_decompressor'

export const useWorkflowProgress = defineStore('workflowProgress', () => {
  const workflowStatuses = ref<
    Record<
      WorkflowProgressItemKey,
      WorkflowProgressData & { object_id: number }
    >
  >({})

  const lastReceived = ref<number>(0)
  const error = ref<boolean | undefined>(false)

  function clearLastReceived(): void {
    lastReceived.value = 0
  }

  function getStatusKey(
    objectType: string,
    objectId: number
  ): WorkflowProgressItemKey {
    return `${objectType}:${objectId}`
  }

  function receiveWorkflowProgress(data: WorkflowProgressResponse): void {
    error.value = data.error

    if (data.statuses) {
      const statuses = decompressWorkflowProgresses(data.statuses) as Array<
        WorkflowProgressData & { object_id: number }
      >

      workflowStatuses.value = keyBy(statuses, (item) =>
        getStatusKey(item.object_type, item.object_id)
      )

      lastReceived.value = Date.now()
    }
  }

  function getStatusForItem(
    objectType: string,
    objectId: number
  ): (WorkflowProgressData & { object_id: number }) | undefined {
    return workflowStatuses.value[getStatusKey(objectType, objectId)]
  }

  function updateItemFromHtml(
    objectType: string,
    objectId: number,
    reconciliationSummaryHtml: string
  ): void {
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = reconciliationSummaryHtml
    const iconElement = tempDiv.querySelector('i')

    if (!iconElement) {
      return
    }

    const key = getStatusKey(objectType, objectId)
    const currentStatus = workflowStatuses.value[key] || {
      object_id: objectId,
      object_type: objectType,
      starred: true,
      has_notes: false,
      has_todos: false,
      has_checks: false,
      has_text_properties: false,
      has_data: true,
      reconciled: false,
      reconciliation_not_necessary: false,
    }

    const classes = iconElement.className
    const isReconciled = classes.includes('i-reconciled')
    const isNoReconNeeded = classes.includes('i-no-reconciliation-needed')
    const hasNoTextProps = classes.includes('no-text-properties')

    workflowStatuses.value[key] = {
      ...currentStatus,
      reconciled: isReconciled,
      reconciliation_not_necessary: isNoReconNeeded,
      has_text_properties: isNoReconNeeded
        ? !hasNoTextProps
        : currentStatus.has_text_properties,
    }

    lastReceived.value = Date.now()
  }

  function toggleStarredStatus(
    objectType: string,
    objectId: number,
    starred: boolean
  ): void {
    const key = getStatusKey(objectType, objectId)
    const currentStatus = workflowStatuses.value[key]

    if (currentStatus) {
      workflowStatuses.value[key] = {
        ...currentStatus,
        starred,
      }
      lastReceived.value = Date.now()
    }
  }

  return {
    clearLastReceived,
    lastReceived,
    receiveWorkflowProgress,
    workflowStatuses,
    getStatusForItem,
    updateItemFromHtml,
    toggleStarredStatus,
    error,
  }
})
